import type { MessageInstance } from 'antd/es/message/interface';
import type { NotificationInstance } from 'antd/es/notification/interface';

interface NotificationConfig {
  message: string;
  description?: string;
  duration?: number;
  placement?: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';
}

interface MessageConfig {
  content: string;
  duration?: number;
}

class NotificationService {
  private notificationApi: NotificationInstance | null = null;

  private messageApi: MessageInstance | null = null;

  init(notificationApi: NotificationInstance, messageApi: MessageInstance) {
    this.notificationApi = notificationApi;
    this.messageApi = messageApi;
  }

  success(config: NotificationConfig) {
    if (!this.notificationApi) {
      return;
    }

    this.notificationApi.success({
      message: config.message,
      description: config.description,
      duration: config.duration ?? 4,
      placement: config.placement ?? 'topRight',
    });
  }

  error(config: NotificationConfig) {
    if (!this.notificationApi) {
      return;
    }

    this.notificationApi.error({
      message: config.message,
      description: config.description,
      duration: config.duration ?? 4,
      placement: config.placement ?? 'topRight',
    });
  }

  warning(config: NotificationConfig) {
    if (!this.notificationApi) {
      return;
    }

    this.notificationApi.warning({
      message: config.message,
      description: config.description,
      duration: config.duration ?? 4,
      placement: config.placement ?? 'topRight',
    });
  }

  info(config: NotificationConfig) {
    if (!this.notificationApi) {
      return;
    }

    this.notificationApi.info({
      message: config.message,
      description: config.description,
      duration: config.duration ?? 4,
      placement: config.placement ?? 'topRight',
    });
  }

  successMessage(config: MessageConfig) {
    if (!this.messageApi) {
      return;
    }

    this.messageApi.success(config.content, config.duration);
  }

  errorMessage(config: MessageConfig) {
    if (!this.messageApi) {
      return;
    }

    this.messageApi.error(config.content, config.duration);
  }

  warningMessage(config: MessageConfig) {
    if (!this.messageApi) {
      return;
    }

    this.messageApi.warning(config.content, config.duration);
  }

  infoMessage(config: MessageConfig) {
    if (!this.messageApi) {
      return;
    }

    this.messageApi.info(config.content, config.duration);
  }

  auth = {
    loginSuccess: (userName?: string) => {
      this.success({
        message: 'Login Successful',
        description: userName
          ? `Welcome back, ${userName}! You have been successfully logged in.`
          : 'Welcome back! You have been successfully logged in.',
      });
    },
    loginError: (error: string) => {
      this.error({
        message: 'Login Failed',
        description: error || 'Invalid credentials. Please check your login details and try again.',
      });
    },
    logoutSuccess: () => {
      this.success({
        message: 'Logged Out',
        description: 'You have been successfully logged out.',
        duration: 3,
      });
    },
    sessionExpired: () => {
      this.warning({
        message: 'Session Expired',
        description: 'Your session has expired. Please log in again.',
        duration: 5,
      });
    },
    corsError: () => {
      this.error({
        message: 'Connection Error',
        description: 'Unable to connect to the server. Please check your network connection and try again.',
        duration: 6,
      });
    },
  };

  api = {
    networkError: () => {
      this.error({
        message: 'Network Error',
        description: 'Unable to connect to the server. Please check your internet connection.',
        duration: 5,
      });
    },
    serverError: () => {
      this.error({
        message: 'Server Error',
        description: 'Something went wrong on our end. Please try again later.',
        duration: 5,
      });
    },
    requestTimeout: () => {
      this.error({
        message: 'Request Timeout',
        description: 'The request took too long to complete. Please try again.',
        duration: 5,
      });
    },
  };

  products = {
    fetchSuccess: (count?: number) => {
      this.success({
        message: 'Fake Store Loaded',
        description:
          typeof count === 'number'
            ? `Loaded ${count} products from Fake Store API.`
            : 'Loaded products from Fake Store API.',
      });
    },
    fetchError: (error?: string) => {
      this.error({
        message: 'Fake Store Request Failed',
        description: error || 'Failed to load Fake Store products. Please try again.',
        duration: 5,
      });
    },
  };
}

export const notificationService = new NotificationService();

export default notificationService;